import React, { useState } from 'react';
import { compose } from 'redux';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';

import { useIntl } from '../../util/reactIntl';
import { useConfiguration } from '../../context/configurationContext';
import { isScrollingDisabled } from '../../ducks/ui.duck';

import { Page, LayoutSingleColumn, Heading, PrimaryButton } from '../../components';
import TopbarContainer from '../TopbarContainer/TopbarContainer';
import FooterContainer from '../FooterContainer/FooterContainer';

import css from './GetMatchedPage.module.css';

// Order and scope of listing fields to include as quiz steps
const QUIZ_FIELD_KEYS = ['specialty', 'type_of_coaching', 'years_of_experience', 'gender'];

// Referral-source options for the final quiz step. `value` is the canonical
// label stored on the user's profile; the displayed label comes from
// translations (GetMatchedPage.referral.option.<id>).
const REFERRAL_PLATFORMS = [
  { id: 'instagram', value: 'Instagram' },
  { id: 'facebook', value: 'Facebook' },
  { id: 'tiktok', value: 'TikTok' },
  { id: 'twitter', value: 'X / Twitter' },
];
// Sentinel used for the "Other" pill; selecting it reveals a free-text input.
const REFERRAL_OTHER = 'Other';
const REFERRAL_OTHER_MAX = 200;

const buildSteps = (listingFields, intl) =>
  QUIZ_FIELD_KEYS.reduce((acc, key) => {
    const fieldConfig = listingFields.find(f => f.key === key);
    if (!fieldConfig) return acc;

    const { schemaType, enumOptions = [], filterConfig, scope } = fieldConfig;
    const isMulti = schemaType === 'multi-enum';

    const options = enumOptions.map(o => ({ label: o.label, value: o.option }));
    if (!isMulti) {
      options.unshift({
        label: intl.formatMessage({ id: 'GetMatchedPage.option.noPreference' }),
        value: null,
      });
    }

    return [
      ...acc,
      {
        id: key,
        title: intl.formatMessage({ id: `GetMatchedPage.step.${key}.title` }),
        subtitle: intl.formatMessage({
          id: isMulti ? 'GetMatchedPage.subtitle.multi' : 'GetMatchedPage.subtitle.single',
        }),
        type: isMulti ? 'multi' : 'single',
        paramKey: scope === 'meta' ? `meta_${key}` : `pub_${key}`,
        searchMode: filterConfig?.searchMode || 'has_any',
        options,
      },
    ];
  }, []);

export const GetMatchedPageComponent = props => {
  const { scrollingDisabled, isAuthenticated, history } = props;
  const intl = useIntl();
  const config = useConfiguration();

  const listingFields = config?.listing?.listingFields || [];
  const baseSteps = buildSteps(listingFields, intl);

  // The referral-source step is shown only to prospective trainees who are
  // about to sign up (i.e. not authenticated). Returning users and coaches
  // never pass through this flow, so they only see the 4 preference steps.
  const referralStep = {
    id: 'referralSource',
    kind: 'referral',
    title: intl.formatMessage({ id: 'GetMatchedPage.step.referralSource.title' }),
    subtitle: intl.formatMessage({ id: 'GetMatchedPage.step.referralSource.subtitle' }),
  };
  const steps = isAuthenticated ? baseSteps : [...baseSteps, referralStep];

  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState({});
  const [referralSelection, setReferralSelection] = useState(null);
  const [referralOther, setReferralOther] = useState('');

  if (steps.length === 0) {
    return null;
  }

  const currentStep = steps[step];
  const totalSteps = steps.length;
  const isLastStep = step === totalSteps - 1;
  const isReferralStep = currentStep.kind === 'referral';

  // The value saved to the new user's profile: a platform label, the trimmed
  // free text when "Other" is chosen, or null when nothing is selected.
  const getReferralValue = () => {
    if (referralSelection === REFERRAL_OTHER) {
      const trimmed = referralOther.trim();
      return trimmed ? trimmed.slice(0, REFERRAL_OTHER_MAX) : null;
    }
    return referralSelection || null;
  };

  // Human-readable answer label(s) for a quiz step, resolved from the step's
  // options. Returns an array for multi steps and a string for single steps,
  // or null when the step is not present in this run.
  const getStepLabels = stepId => {
    const s = steps.find(st => st.id === stepId);
    if (!s) return null;
    const answer = answers[s.id];
    if (s.type === 'multi') {
      const values = Array.isArray(answer) ? answer : [];
      return values.map(v => s.options.find(o => o.value === v)?.label).filter(Boolean);
    }
    if (!(s.id in answers)) return '';
    return s.options.find(o => o.value === answer)?.label || '';
  };

  // The submission stored on the new user's profile at signup. Values are the
  // display labels so the weekly admin email needs no server-side translation.
  // Keys map to the report columns: Goals, Preference, Coach Experience,
  // Coach Gender, Source.
  const buildQuizAnswers = () => ({
    goals: getStepLabels('specialty') || [],
    preference: getStepLabels('type_of_coaching') || [],
    coachExperience: getStepLabels('years_of_experience') || '',
    coachGender: getStepLabels('gender') || '',
    source: getReferralValue() || '',
  });

  const toggleMulti = value => {
    const current = answers[currentStep.id] || [];
    const next = current.includes(value)
      ? current.filter(v => v !== value)
      : [...current, value];
    setAnswers({ ...answers, [currentStep.id]: next });
  };

  const setSingle = value => {
    setAnswers({ ...answers, [currentStep.id]: value });
  };

  const isSelected = value => {
    if (currentStep.type === 'multi') {
      return (answers[currentStep.id] || []).includes(value);
    }
    return answers[currentStep.id] === value;
  };

  // referral: optional, but if "Other" is chosen the free-text box must be filled
  // multi: at least one option must be selected
  // single: undefined = nothing touched yet; null = "No preference" explicitly chosen (valid)
  const referralComplete =
    referralSelection !== REFERRAL_OTHER || referralOther.trim().length > 0;
  const canProceed = isReferralStep
    ? referralComplete
    : currentStep.type === 'multi'
    ? (answers[currentStep.id] || []).length > 0
    : currentStep.id in answers;

  const handleNext = () => {
    if (!isLastStep) {
      setStep(step + 1);
      return;
    }

    const params = {};
    steps.forEach(s => {
      if (s.kind === 'referral') return;
      const answer = answers[s.id];
      if (s.type === 'multi' && Array.isArray(answer) && answer.length > 0) {
        params[s.paramKey] = `${s.searchMode}:${answer.join(',')}`;
      } else if (s.type === 'single' && answer != null) {
        params[s.paramKey] = answer;
      }
    });

    const queryString = new URLSearchParams(params).toString();
    const searchUrl = `/s${queryString ? `?${queryString}` : ''}`;

    if (isAuthenticated) {
      history.push(searchUrl);
    } else {
      // Send unauthenticated users to signup; they'll be redirected back to
      // searchUrl after they finish signing up. AuthenticationPage reads
      // location.state.from and redirects there once auth completes, and reads
      // location.state.referralSource + location.state.quizAnswers to save them
      // onto the new user's profile (the weekly admin report reads quizAnswers).
      const referralSource = getReferralValue();
      const referralMaybe = referralSource ? { referralSource } : {};
      history.push('/signup/trainee', {
        from: searchUrl,
        ...referralMaybe,
        quizAnswers: buildQuizAnswers(),
      });
    }
  };

  return (
    <Page
      title={intl.formatMessage({ id: 'GetMatchedPage.title' })}
      scrollingDisabled={scrollingDisabled}
    >
      <LayoutSingleColumn topbar={<TopbarContainer />} footer={<FooterContainer />}>
        <div className={css.root}>
          <div className={css.progress}>
            <span className={css.progressLabel}>
              {intl.formatMessage(
                { id: 'GetMatchedPage.progress' },
                { current: step + 1, total: totalSteps }
              )}
            </span>
            <div className={css.progressBar}>
              <div
                className={css.progressFill}
                style={{ width: `${((step + 1) / totalSteps) * 100}%` }}
              />
            </div>
          </div>

          <Heading as="h1" rootClassName={css.title}>
            {currentStep.title}
          </Heading>
          <p className={css.subtitle}>{currentStep.subtitle}</p>

          {isReferralStep ? (
            <div className={css.referral}>
              <div className={css.referralRow}>
                {REFERRAL_PLATFORMS.map(option => {
                  const selected = referralSelection === option.value;
                  return (
                    <button
                      key={option.id}
                      type="button"
                      className={selected ? `${css.chip} ${css.chipSelected}` : css.chip}
                      onClick={() => setReferralSelection(option.value)}
                    >
                      {intl.formatMessage({ id: `GetMatchedPage.referral.option.${option.id}` })}
                    </button>
                  );
                })}
              </div>
              <div className={css.referralRow}>
                <button
                  type="button"
                  className={
                    referralSelection === REFERRAL_OTHER
                      ? `${css.chip} ${css.chipSelected}`
                      : css.chip
                  }
                  onClick={() => setReferralSelection(REFERRAL_OTHER)}
                >
                  {intl.formatMessage({ id: 'GetMatchedPage.referral.option.other' })}
                </button>
              </div>
              {referralSelection === REFERRAL_OTHER && (
                <input
                  type="text"
                  className={css.referralOtherInput}
                  maxLength={REFERRAL_OTHER_MAX}
                  value={referralOther}
                  onChange={e => setReferralOther(e.target.value)}
                  placeholder={intl.formatMessage({
                    id: 'GetMatchedPage.referral.otherPlaceholder',
                  })}
                />
              )}
            </div>
          ) : (
            <div className={css.optionsChips}>
              {currentStep.options.map(option => {
                const key = option.value ?? '__none__';
                const selected = isSelected(option.value);
                return (
                  <button
                    key={key}
                    type="button"
                    className={selected ? `${css.chip} ${css.chipSelected}` : css.chip}
                    onClick={() => {
                      if (currentStep.type === 'multi') {
                        if (option.value !== null) toggleMulti(option.value);
                      } else {
                        setSingle(option.value);
                      }
                    }}
                  >
                    {option.label}
                  </button>
                );
              })}
            </div>
          )}

          <div className={css.actions}>
            {step > 0 && (
              <button
                type="button"
                className={css.backButton}
                onClick={() => setStep(step - 1)}
              >
                {intl.formatMessage({ id: 'GetMatchedPage.action.back' })}
              </button>
            )}
            <PrimaryButton
              className={css.nextButton}
              type="button"
              onClick={handleNext}
              disabled={!canProceed}
            >
              {intl.formatMessage({
                id: isLastStep
                  ? 'GetMatchedPage.action.findCoach'
                  : 'GetMatchedPage.action.next',
              })}
            </PrimaryButton>
          </div>
        </div>
      </LayoutSingleColumn>
    </Page>
  );
};

const mapStateToProps = state => ({
  scrollingDisabled: isScrollingDisabled(state),
  isAuthenticated: state.auth.isAuthenticated,
});

const GetMatchedPage = compose(withRouter, connect(mapStateToProps))(GetMatchedPageComponent);

export default GetMatchedPage;
